import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useTmaisBadge } from '@/features/tmaisBadges/hooks/useTmaisBadge'
import { useUpdateTmaisBadge } from '@/features/tmaisBadges/hooks/useUpdateTmaisBadge'
import { TmaisBadgeForm } from '@/features/tmaisBadges/components/TmaisBadgeForm'
import { TmaisBadge } from '@/features/tmaisBadges/types/tmaisBadge.type'
import { TmaisBadgeFormSchema, updateTmaisBadgeSchema } from '@/features/tmaisBadges/schemas/form.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useGoBack } from '@/common/hooks/useGoBack' 

const EditTmaisBadge = () => {
  const { id } = useParams<{ id: string }>()
  const goBack = useGoBack()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const canUpdate = isUserPermissionsPending || hasUserPermission('Admin::Tmais::Badges::Update')

  const form = useForm<TmaisBadgeFormSchema>({
    resolver: zodResolver(updateTmaisBadgeSchema),
    defaultValues: {
      display_name: '',
      background_color: '#000000',
      text_color: '#FFFFFF',
      icon: null,
    },
  })

  const { reset, handleSubmit, setError } = form

  const {
    data: badge,
    isPending: badgeIsPending,
    error: badgeError = {} as any,
    refetch,
  } = useTmaisBadge<TmaisBadge>({ id })

  useEffect(() => {
    if (badge) {
      reset({
        display_name: badge.display_name,
        background_color: badge.background_color,
        text_color: badge.text_color,
        icon: badge.icon?.url || null,
      })
    }
  }, [badge, reset])

  const { mutate: updateBadge, isPending: updateIsPending } = useUpdateTmaisBadge({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisBadges'] })
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['tmaisBadge', id] })
      }
      
      goBack({ fallback: id ? `/tmais-badges/${id}` : '/tmais-badges' })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<TmaisBadgeFormSchema> = (values) => {
    if (!id) return
    updateBadge({ id, form: values })
  }

  const statusCode = !canUpdate ? 403 : badgeError?.status

  return (
    <ErrorMessageWrapper className="h-[calc(100vh-128px)]" statusCode={statusCode} refetchFunction={refetch}>
      {badgeIsPending && canUpdate ? (
        <Box>Carregando...</Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GoBackButton fallback="/tmais-badges" />
              <p className="text-white-400 font-normal">Destaques para itens</p>
              <ChevronsRight className="text-white-400" />
              <h2>Editar destaque</h2>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline-error"
                type="button"
                onClick={() => goBack({ fallback: id ? `/tmais-badges/${id}` : '/tmais-badges' })}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                isLoading={updateIsPending}
                disabled={updateIsPending}
                operationKlassToCheckPermission="Admin::Tmais::Badges::Update"
              >
                Salvar Alterações
              </Button>
            </div>
          </div>

          <TmaisBadgeForm form={form} isSubmitting={updateIsPending} />

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </form>
      )}
    </ErrorMessageWrapper>
  )
}

export default EditTmaisBadge